import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Auth } from "../auth/auth";
import cx from "classnames";
import { ErrorMessage, Field, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";

import "./sign-in.scss";
import {
  useEventMutation,
  useLoginMutation,
  useLoginWithMsisdnMutation,
  useOtpGenerateForLoginMutation,
  useOtpLoginStatusMutation,
  useOtpValidateLoginMutation,
} from "../../data-access/store/services/auth.service";
import { useCallback, useEffect, useRef, useState } from "react";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";
import { selectIsAuthenticated } from "../../../auth/data-access/store/authSlice";
import Modal from "../../../shared/ui/modal/modal";
// import { UpstreamServiceBanner } from "../../../shared/ui/upstream-service-banner";
import { NoAccountFound } from "../../../shared/ui/modal-notifications/no-account-found";
import { GenericError } from "../../../shared/ui/modal-notifications/generic-error";
import { WrongCredentials } from "../../../shared/ui/modal-notifications/wrong-credentials";

const HYDRA_FALLBACK_FLAG = "hydra_fallback_msisdn_only";
const HYDRA_NO_ACCOUNT_FLAG = "hydra_no_account";

export function SignIn() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [login, { isLoading: loginIsLoading, error: loginError }] =
    useLoginMutation();
  const [
    loginWithMsisdn,
    { isLoading: loginWithMsisdnIsLoading, error: loginWithMsisdnError },
  ] = useLoginWithMsisdnMutation();
  const [
    otpGenerateForLogin,
    { isLoading: otpGenerateIsLoading, error: otpGenerateError },
  ] = useOtpGenerateForLoginMutation();
  const [
    otpValidateLogin,
    { isLoading: otpValidateIsLoading, error: otpValidateError },
  ] = useOtpValidateLoginMutation();
  const [otpLoginStatus] = useOtpLoginStatusMutation();
  // OTP manual login is a two-step flow: enter MSISDN, then enter the OTP code.
  const [otpStep, setOtpStep] = useState<"phone" | "otp">("phone");
  const [otpMsisdn, setOtpMsisdn] = useState("");
  // Opaque handle for the current login attempt, returned by OTP generate. Carries
  // the Curry uid on the server side, which OTP validate has to be sent with and
  // which the notification quotes back — so every later call must include it.
  const [otpRef, setOtpRef] = useState<string | undefined>(undefined);
  // True while waiting on the Curry notification to resolve a submitted code.
  const [isAwaitingResult, setIsAwaitingResult] = useState(false);
  // True once an entered OTP was rejected — drives the "Resend code" link.
  const [otpInvalid, setOtpInvalid] = useState(false);
  // Stops an in-flight poll when the component unmounts or the user starts over.
  const pollAbortedRef = useRef(false);
  // Handle to the Formik instance, needed to clear the "otp" field imperatively
  // from failure paths that live outside the render prop.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formikRef = useRef<FormikProps<any>>(null);
  const init = useAppSelector(selectInit);
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);
  const [event] = useEventMutation();
  const location = useLocation();
  const loginForPurchase = location.state === "redirectedFromStore";
  const loginForUnsubscribe = location.state === "redirectedFromProfile";
  const baseLoginType = init?.users?.authType || "msisdn";
  const [isForcedMsisdnOnly, setIsForcedMsisdnOnly] = useState(false);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);
  const [showGenericErrorModal, setShowGenericErrorModal] = useState(false);
  const [showWrongCredentialsModal, setShowWrongCredentialsModal] =
    useState(false);

  // Check for Hydra UUID fallback flag and force MSISDN-only login
  // Also show the subscribe modal if HE login failed with "no account".
  useEffect(() => {
    const hydraFallbackFlag = sessionStorage.getItem(HYDRA_FALLBACK_FLAG);
    if (hydraFallbackFlag) {
      setIsForcedMsisdnOnly(true);
    }

    const noAccountFlag = sessionStorage.getItem(HYDRA_NO_ACCOUNT_FLAG);
    if (noAccountFlag) {
      sessionStorage.removeItem(HYDRA_NO_ACCOUNT_FLAG);
      setShowNoAccountModal(true);
    }
  }, []);

  // Clear fallback flag when user successfully authenticates
  useEffect(() => {
    if (isAuthenticated && isForcedMsisdnOnly) {
      sessionStorage.removeItem(HYDRA_FALLBACK_FLAG);
      setIsForcedMsisdnOnly(false);
    }
  }, [isAuthenticated, isForcedMsisdnOnly]);

  // Determine effective login type (MSISDN-only if fallback flag is set)
  const loginType = isForcedMsisdnOnly ? "msisdn" : baseLoginType;

  useEffect(() => {
    if (loginError) {
      const errorMsg =
        (loginError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      const status = (loginError as { status?: number })?.status;
      const lower = errorMsg.toLowerCase();
      const isWrongCredentials =
        status === 401 ||
        status === 403 ||
        lower.includes("invalid credentials") ||
        lower.includes("wrong credentials") ||
        lower.includes("wrong password") ||
        lower.includes("incorrect password") ||
        lower.includes("invalid password") ||
        lower.includes("unauthorized") ||
        lower.includes("invalid_grant");

      if (isWrongCredentials) {
        const invalidCredentialsErrorMsg = checkTranslationKey(
          translations[lang]?.Invalid_Credentials,
          "Your Username or Password is wrong."
        );
        dispatch(
          setMessage({ content: invalidCredentialsErrorMsg, type: "error" })
        );
      } else {
        setShowGenericErrorModal(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginError]);

  useEffect(() => {
    if (loginWithMsisdnError) {
      const errorMsg =
        (loginWithMsisdnError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      if (errorMsg) {
        // Check if error is about no account found
        const isNoAccountError =
          errorMsg.toLowerCase().includes("not found") ||
          errorMsg.toLowerCase().includes("no account") ||
          errorMsg.toLowerCase().includes("account does not exist") ||
          errorMsg.toLowerCase().includes("could not login");

        if (isNoAccountError) {
          // Show the no account modal instead of error message
          setShowNoAccountModal(true);
        } else {
          // Show other errors as messages
          dispatch(setMessage({ content: errorMsg, type: "error" }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginWithMsisdnError]);

  // Abandon any in-flight poll if the user navigates away mid-login.
  useEffect(() => {
    return () => {
      pollAbortedRef.current = true;
    };
  }, []);

  // Sends the user back to the MSISDN step. Used when the attempt is no longer
  // resolvable server-side (its context expired), so a new code has to be issued.
  const restartOtpFlow = useCallback(
    (message: string) => {
      pollAbortedRef.current = true;
      setIsAwaitingResult(false);
      setOtpRef(undefined);
      setOtpInvalid(false);
      setOtpStep("phone");
      formikRef.current?.setFieldValue("otp", "");
      dispatch(setMessage({ content: message, type: "error" }));
    },
    [dispatch]
  );

  // Resend is the same OTP Generate call with a new uid — there is no separate
  // regenerate endpoint. Passing the existing otpRef keeps the handle stable so an
  // in-progress attempt is replaced rather than duplicated.
  const resendOtp = useCallback(() => {
    if (!otpMsisdn || otpGenerateIsLoading) {
      return;
    }

    pollAbortedRef.current = true;
    setIsAwaitingResult(false);

    otpGenerateForLogin({ msisdn: otpMsisdn, regenerate: true, otpRef })
      .unwrap()
      .then((response) => {
        if (response?.otpRef) {
          setOtpRef(response.otpRef);
        }
        setOtpInvalid(false);
        formikRef.current?.setFieldValue("otp", "");
      })
      .catch(() => {
        /* handled by otpGenerateError effect */
      });
  }, [otpGenerateForLogin, otpGenerateIsLoading, otpMsisdn, otpRef]);

  const failOtpAttempt = useCallback(() => {
    // Same treatment as a synchronously rejected code: tell the user, keep them on
    // the code step, and reveal "Resend code".
    setShowWrongCredentialsModal(true);
    setOtpInvalid(true);
    formikRef.current?.setFieldValue("otp", "");
  }, []);

  /**
   * Waits for an asynchronous OTP validation outcome.
   *
   * Reached only when the server answered `pending`, which means Upstream did not
   * return the ise2 on the validate response and it is coming on the Curry
   * notification instead. Until that inbound call lands there is nothing to show
   * the user, so the attempt handle is polled until it resolves or the deadline
   * passes. Transient poll errors are swallowed on purpose — a single failed
   * request should not abandon a login that is about to succeed.
   */
  const pollForOtpResult = useCallback(
    async (ref: string, intervalSeconds?: number, timeoutSeconds?: number) => {
      const intervalMs = Math.max(1, intervalSeconds || 1) * 1000;
      const deadline = Date.now() + Math.max(5, timeoutSeconds || 25) * 1000;

      pollAbortedRef.current = false;
      setIsAwaitingResult(true);

      try {
        while (!pollAbortedRef.current && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, intervalMs));

          if (pollAbortedRef.current) {
            return;
          }

          try {
            const result = await otpLoginStatus({ otpRef: ref }).unwrap();

            if (result.apikey) {
              localStorage.setItem("fromLogin", "true");
              navigate("/");
              return;
            }
          } catch (error) {
            const status = (error as { status?: number })?.status;
            const message =
              (error as { data?: { error?: string } })?.data?.error || "";
            const lower = message.toLowerCase();

            if (status === 409) {
              restartOtpFlow(
                checkTranslationKey(
                  translations[lang]?.The_code_has_expired,
                  "The code has expired. Please request a new one."
                )
              );
              return;
            }

            if (status === 403) {
              if (
                lower.includes("not found") ||
                lower.includes("no account") ||
                lower.includes("account does not exist")
              ) {
                setShowNoAccountModal(true);
              } else {
                failOtpAttempt();
              }
              return;
            }

            // Anything else (network blip, 5xx) — keep polling until the deadline.
          }
        }

        if (!pollAbortedRef.current) {
          // No outcome inside the window. The spec's answer to a validation that
          // does not succeed is a message and a restart, so treat it as such
          // rather than leaving the user on a spinner.
          failOtpAttempt();
        }
      } finally {
        setIsAwaitingResult(false);
      }
    },
    [
      failOtpAttempt,
      lang,
      navigate,
      otpLoginStatus,
      restartOtpFlow,
      translations,
    ]
  );

  // OTP validation / login errors (manual OTP flow).
  useEffect(() => {
    if (otpValidateError) {
      const errorMsg =
        (otpValidateError as { data?: { error?: string; message?: string } })
          ?.data?.error ||
        (otpValidateError as { data?: { message?: string } })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      const lower = errorMsg.toLowerCase();
      const status = (otpValidateError as { status?: number })?.status;

      if (status === 409) {
        // The server no longer holds this attempt, so the uid it would send to
        // Curry is gone — a new code has to be issued.
        restartOtpFlow(
          checkTranslationKey(
            translations[lang]?.The_code_has_expired,
            "The code has expired. Please request a new one."
          )
        );
        return;
      }

      const isNoAccountError =
        lower.includes("not found") ||
        lower.includes("no account") ||
        lower.includes("account does not exist");

      if (isNoAccountError) {
        // Hydra found no account for this number — prompt to sign up.
        setShowNoAccountModal(true);
      } else {
        // OTP validation failed: show the wrong-credentials modal, keep the user
        // on the code step and reveal the "Resend code" link.
        setShowWrongCredentialsModal(true);
        setOtpInvalid(true);
        formikRef.current?.setFieldValue("otp", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValidateError]);

  useEffect(() => {
    if (otpGenerateError) {
      dispatch(
        setMessage({
          content: checkTranslationKey(
            translations[lang]?.Could_not_send_the_code,
            "Could not send the code. Please try again."
          ),
          type: "error",
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpGenerateError]);

  const prefix = init?.users?.msisdn?.prefix || "";
  const phoneMaxLength = init?.users?.msisdn?.maxLength || 9;
  const phoneMinLength = init?.users?.msisdn?.minLength || 9;
  const pinMaxLength = init?.users?.pin?.maxLength || 6;
  const pinMinLength = init?.users?.pin?.minLength || 4;

  const SignInSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(
        /^[0-9]+$/,
        checkTranslationKey(
          translations[lang]?.The_phone_number_must_be_only_digits,
          "The phone number must be only digits"
        )
      )
      .min(
        phoneMinLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_short,
          "The phone number is too short."
        )
      )
      .max(
        phoneMaxLength,
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_too_long,
          "The phone number is too long."
        )
      )
      .required(
        checkTranslationKey(
          translations[lang]?.The_phone_number_is_required,
          "The phone number is required."
        )
      ),
    ...(loginType === "password"
      ? {
          password: Yup.string()
            .matches(
              /^[0-9]+$/,
              checkTranslationKey(
                translations[lang]?.The_password_must_be_only_digits,
                "The password must be only digits"
              )
            )
            .min(
              pinMinLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_short,
                "The password is too short."
              )
            )
            .max(
              pinMaxLength,
              checkTranslationKey(
                translations[lang]?.The_password_is_too_long,
                "The password is too long."
              )
            )
            .required(
              checkTranslationKey(
                translations[lang]?.The_password_is_required,
                "The password is required."
              )
            ),
        }
      : {}),
    ...(loginType === "otp" && otpStep === "otp"
      ? {
          otp: Yup.string()
            .matches(
              /^[0-9]+$/,
              checkTranslationKey(
                translations[lang]?.The_code_must_be_only_digits,
                "The code must be only digits"
              )
            )
            .min(
              pinMinLength,
              checkTranslationKey(
                translations[lang]?.The_code_is_too_short,
                "The code is too short."
              )
            )
            .max(
              pinMaxLength,
              checkTranslationKey(
                translations[lang]?.The_code_is_too_long,
                "The code is too long."
              )
            )
            .required(
              checkTranslationKey(
                translations[lang]?.The_code_is_required,
                "The code is required."
              )
            ),
        }
      : {}),
  });

  return (
    <div className="auth-page">
      <Auth
        className="auth-shell-content"
        header={
          <Title>
            {checkTranslationKey(translations[lang]?.Sign_In, "Sign In")}
          </Title>
        }
        hasFooter
      >
        <div className="form-title form-title-login">
          {checkTranslationKey(
            translations[lang]?.Enter_your_BattleUp_account,
            "Enter your BattleUp account"
          )}
        </div>
        {otpStep !== "otp" && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]?.Telephone_number_for_your_account,
                "Telephone number for your account"
              )}
            </span>
          </div>
        )}
        {loginForPurchase && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_purchase,
                "Please enter your credentials to proceed with the purchase"
              )}
            </span>
          </div>
        )}
        {loginForUnsubscribe && (
          <div className="notice">
            <span>
              {checkTranslationKey(
                translations[lang]
                  ?.Please_enter_your_credentials_to_proceed_with_the_unsubscribe,
                "Please enter your credentials to proceed with the unsubscribe"
              )}
            </span>
          </div>
        )}
        <div className="form-container">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={
              loginType === "password"
                ? { phone: "", password: "" }
                : loginType === "otp"
                ? { phone: "", otp: "" }
                : { phone: "" }
            }
            validationSchema={SignInSchema}
            onSubmit={(values) => {
              if (loginType === "password" && values.password) {
                login({
                  msisdn: prefix + values.phone,
                  password: values.password,
                });
              } else if (loginType === "msisdn") {
                loginWithMsisdn({
                  msisdn: prefix + values.phone,
                });
              } else if (loginType === "otp") {
                if (otpStep === "phone") {
                  // Step 1: request the OTP SMS, then reveal the code field. The
                  // otpRef returned here identifies the attempt for every
                  // subsequent call.
                  const fullMsisdn = prefix + values.phone;
                  setOtpMsisdn(fullMsisdn);
                  setOtpInvalid(false);
                  setOtpRef(undefined);
                  otpGenerateForLogin({ msisdn: fullMsisdn })
                    .unwrap()
                    .then((response) => {
                      setOtpRef(response?.otpRef);
                      setOtpStep("otp");
                    })
                    .catch(() => {
                      /* handled by otpGenerateError effect */
                    });
                  event({ type: "LOGIN" });
                  localStorage.setItem("fromLogin", "true");
                  return;
                }
                // Step 2: submit the code. On an immediate success the auth state
                // is updated by the matcher; the sign-in route permits the
                // resulting "userToken" role, so navigate to the home page
                // explicitly rather than relying on the route guard to redirect.
                //
                // A `pending` response means the outcome is arriving on the Curry
                // notification instead, on a request we are not part of — wait for
                // it by polling the attempt handle.
                otpValidateLogin({
                  msisdn: otpMsisdn,
                  otp: (values as { otp?: string }).otp || "",
                  otpRef,
                })
                  .unwrap()
                  .then((response) => {
                    if (response?.apikey) {
                      localStorage.setItem("fromLogin", "true");
                      navigate("/");
                      return;
                    }

                    const ref = response?.otpRef || otpRef;

                    if (!ref) {
                      // Neither an apikey nor anything to poll: nothing to wait for.
                      failOtpAttempt();
                      return;
                    }

                    setOtpRef(ref);
                    void pollForOtpResult(
                      ref,
                      response?.pollInterval,
                      response?.pollTimeout
                    );
                  })
                  .catch(() => {
                    /* handled by otpValidateError effect */
                  });
                return;
              }
              event({ type: "LOGIN" });
              localStorage.setItem("fromLogin", "true");
            }}
          >
            {({ errors, touched }) => (
              <>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Form {...({} as any)}>
                  {!(loginType === "otp" && otpStep === "otp") && (
                    <div
                      className={cx("form-input-row", {
                        danger: errors.phone && touched.phone,
                        success: !errors.phone,
                      })}
                    >
                      <div className="prefix-input">
                        <span className="prefix">+{prefix}</span>
                        <Field
                          type="tel"
                          name="phone"
                          className="half-input"
                          placeholder={checkTranslationKey(
                            translations[lang]?.Your_phone_number,
                            "Your phone number"
                          )}
                          autoComplete="off"
                          maxLength={phoneMaxLength}
                          required
                        />
                      </div>
                      <ErrorMessage name="phone">
                        {(msg) => (
                          <div className="error-msg">
                            <img
                              src="/img/icns/alert-fill.svg"
                              alt="alert"
                              className="color-svg"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  )}

                  {loginType === "password" && (
                    <div
                      className={cx("form-input-row", {
                        danger: errors.password && touched.password,
                      })}
                    >
                      <div className="notice">
                        <span>
                          {checkTranslationKey(
                            translations[lang]?.Password,
                            "Password"
                          )}
                        </span>
                      </div>
                      <Field
                        autoComplete="off"
                        className="full-input"
                        type="password"
                        name="password"
                        placeholder={checkTranslationKey(
                          translations[lang]?.Your_password,
                          "Your password"
                        )}
                        maxLength={pinMaxLength}
                      />
                      <ErrorMessage name="password">
                        {(msg) => (
                          <div className="error-msg">
                            <img
                              src="/img/icns/alert-fill.svg"
                              alt="alert"
                              className="color-svg"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                    </div>
                  )}

                  {loginType === "otp" && otpStep === "otp" && (
                    <div
                      className={cx("form-input-row", {
                        danger: errors.otp && touched.otp,
                      })}
                    >
                      <div className="notice">
                        <span>
                          {checkTranslationKey(
                            translations[lang]
                              ?.Enter_the_code_sent_to_your_phone,
                            "Enter the code sent to your phone"
                          )}
                        </span>
                      </div>
                      <Field
                        autoComplete="off"
                        className="full-input"
                        type="tel"
                        name="otp"
                        placeholder={checkTranslationKey(
                          translations[lang]?.Your_code,
                          "Your code"
                        )}
                        maxLength={pinMaxLength}
                      />
                      <ErrorMessage name="otp">
                        {(msg) => (
                          <div className="error-msg">
                            <img
                              src="/img/icns/alert-fill.svg"
                              alt="alert"
                              className="color-svg"
                            />
                            {msg}
                          </div>
                        )}
                      </ErrorMessage>
                      {otpInvalid && (
                        <div
                          className="resend-area"
                          style={{ marginTop: "16px", textAlign: "center" }}
                        >
                          <a
                            className="resend-otp"
                            role="button"
                            tabIndex={0}
                            style={{
                              color: "#00B1B2",
                              fontFamily: "Nunito",
                              fontWeight: 800,
                              fontSize: "16px",
                              lineHeight: "100%",
                              letterSpacing: "0px",
                              textAlign: "center",
                              verticalAlign: "middle",
                              fontVariantNumeric:
                                "lining-nums proportional-nums",
                              textDecoration: "underline",
                              textDecorationStyle: "solid",
                              textUnderlineOffset: "0",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              resendOtp();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                resendOtp();
                              }
                            }}
                          >
                            {checkTranslationKey(
                              translations[lang]?.Resend_OTP,
                              "Resend code"
                            )}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <div className="submit">
                      {/* @ts-ignore */}
                      <Button
                        buttonType="success"
                        type="submit"
                        value="Submit"
                        disabled={
                          loginIsLoading ||
                          loginWithMsisdnIsLoading ||
                          otpGenerateIsLoading ||
                          otpValidateIsLoading ||
                          isAwaitingResult
                        }
                      >
                        {loginIsLoading ||
                        otpGenerateIsLoading ||
                        otpValidateIsLoading ||
                        isAwaitingResult
                          ? checkTranslationKey(
                              translations[lang]?.Signing_in,
                              "Signing in..."
                            )
                          : loginType === "otp" && otpStep === "otp"
                          ? checkTranslationKey(
                              translations[lang]?.Login,
                              "Login"
                            )
                          : loginType === "otp"
                          ? checkTranslationKey(
                              translations[lang]?.Send_OTP,
                              "Send OTP"
                            )
                          : checkTranslationKey(
                              translations[lang]?.Sign_in,
                              "Sign in"
                            )}
                      </Button>
                    </div>
                    {/*<div className="forgot-area">*/}
                    {/*  /!*<p*!/*/}
                    {/*  /!*  style={{*!/*/}
                    {/*  /!*    paddingTop: "8px",*!/*/}
                    {/*  /!*    paddingBottom: "8px",*!/*/}
                    {/*  /!*    fontWeight: "bold",*!/*/}
                    {/*  /!*    fontSize: "14px",*!/*/}
                    {/*  /!*  }}*!/*/}
                    {/*  /!*>*!/*/}
                    {/*  /!*  {checkTranslationKey(*!/*/}
                    {/*  /!*    translations[lang]?.Signing_in_cost,*!/*/}
                    {/*  /!*    "Cost: 1.50 GHS/Day"*!/*/}
                    {/*  /!*  )}*!/*/}
                    {/*  /!*</p>*!/*/}
                    {/*  <Link*/}
                    {/*    to="/auth/forgot-password"*/}
                    {/*    className="forgot-password"*/}
                    {/*  >*/}
                    {/*    {checkTranslationKey(*/}
                    {/*      translations[lang]?.Forgot_your_password,*/}
                    {/*      "Forgot your password?"*/}
                    {/*    )}*/}
                    {/*  </Link>*/}
                    {/*</div>*/}
                  </div>
                </Form>
              </>
            )}
          </Formik>
        </div>
      </Auth>

      {!loginForPurchase
        ? !loginForUnsubscribe && (
            <div
              className="no-account-area content"
              style={{ marginTop: "100px" }}
            >
              <div className="no-account" style={{ color: "#303030" }}>
                {checkTranslationKey(
                  translations[lang]?.Don_t_have_an_account,
                  "Don't have an account?"
                )}
              </div>
              <div className="button-submit">
                <Button
                  onClick={() => {
                    window.open(
                      process.env.REACT_APP_EXTERNAL_SUBSCRIBE_URL,
                      "_blank"
                    );
                  }}
                  buttonType="white"
                  small
                >
                  {checkTranslationKey(translations[lang]?.Sign_up, "Sign up")}
                </Button>
              </div>
            </div>
          )
        : null}

      {/*<div className="stream-service">*/}
      {/*  <UpstreamServiceBanner />*/}
      {/*</div>*/}

      <Modal
        show={showNoAccountModal}
        setShow={setShowNoAccountModal}
        hideCloseButton={false}
      >
        <NoAccountFound
          onButtonClick={() => {
            setShowNoAccountModal(false);
            window.open(process.env.REACT_APP_EXTERNAL_SUBSCRIBE_URL, "_self");
          }}
        />
      </Modal>

      <Modal
        show={showGenericErrorModal}
        setShow={setShowGenericErrorModal}
        hideCloseButton={false}
      >
        <GenericError
          onButtonClick={() => {
            setShowGenericErrorModal(false);
            navigate("/");
          }}
        />
      </Modal>

      <Modal
        show={showWrongCredentialsModal}
        setShow={setShowWrongCredentialsModal}
        hideCloseButton={false}
      >
        <WrongCredentials
          buttonText={checkTranslationKey(translations[lang]?.Login, "Login")}
          onButtonClick={() => {
            // Start over: close the modal and return to the MSISDN step.
            pollAbortedRef.current = true;
            setShowWrongCredentialsModal(false);
            setIsAwaitingResult(false);
            setOtpInvalid(false);
            setOtpMsisdn("");
            setOtpRef(undefined);
            setOtpStep("phone");
            formikRef.current?.setFieldValue("otp", "");
          }}
        />
      </Modal>
    </div>
  );
}

export default SignIn;
